<script setup lang="ts">
/**
 * Botón flotante de feedback — visible en TODAS las pantallas (montado en
 * App.vue). El operario toca 💬, escribe el problema en sus palabras y envía.
 * La nota queda en la base con usuario + pantalla + fecha. Claude las revisa
 * en cada sesión de trabajo y las va resolviendo.
 */
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';

const route = useRoute();
const auth = useAuthStore();

const open = ref(false);
const text = ref('');
const sending = ref(false);
const sent = ref(false);

async function submit() {
  if (text.value.trim().length < 3 || sending.value) return;
  sending.value = true;
  try {
    await api.post('/v1/feedback', {
      text: text.value.trim(),
      route: route.fullPath,
    });
    sent.value = true;
    text.value = '';
    // Cerramos solo después de mostrar la confirmación un instante.
    setTimeout(() => {
      open.value = false;
      sent.value = false;
    }, 1400);
  } catch {
    alert('No se pudo enviar la nota. Probá de nuevo en un rato.');
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <!-- Solo visible logueado -->
  <template v-if="auth.isAuthenticated">
    <!-- Botón flotante -->
    <button
      v-if="!open"
      class="fixed bottom-20 right-3 z-40 w-12 h-12 rounded-full bg-gray-900/80 text-white text-xl shadow-lg flex items-center justify-center active:scale-95 transition"
      aria-label="Reportar un problema"
      @click="open = true"
    >
      💬
    </button>

    <!-- Modal -->
    <div
      v-if="open"
      class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
      @click.self="open = false"
    >
      <div class="bg-white rounded-xl w-full max-w-md p-4 safe-bottom">
        <template v-if="!sent">
          <h2 class="text-lg font-bold text-gray-900 mb-1">💬 Reportar un problema</h2>
          <p class="text-sm text-gray-600 mb-3">
            Contá qué te molestó o qué no funcionó. Lo levantamos y lo arreglamos.
          </p>
          <textarea
            v-model="text"
            rows="4"
            class="w-full border-2 border-gray-300 focus:border-brand-500 rounded-lg p-3 text-base"
            placeholder="Ej: cuando confirmo la cantidad se queda pensando…"
            autofocus
          />
          <div class="flex gap-2 mt-3">
            <button class="btn-secondary flex-1" :disabled="sending" @click="open = false">
              Cancelar
            </button>
            <button
              class="flex-1 bg-brand-600 hover:bg-brand-700 text-white font-semibold py-2 rounded-lg disabled:opacity-50"
              :disabled="text.trim().length < 3 || sending"
              @click="submit"
            >
              {{ sending ? 'Enviando…' : 'Enviar nota' }}
            </button>
          </div>
        </template>
        <template v-else>
          <div class="text-center py-6">
            <div class="text-5xl mb-2">✅</div>
            <p class="font-semibold text-gray-900">¡Nota enviada!</p>
            <p class="text-sm text-gray-600 mt-1">Gracias — la vamos a trabajar.</p>
          </div>
        </template>
      </div>
    </div>
  </template>
</template>
